import { z } from "zod";

// Static coach data for display in the debug panel
export const CoachMatchingDebugDataSchema = z.object({
  eligibility: z.object({
    hasProfilePhoto: z.boolean(),
    hasGhlIntegration: z.boolean(),
    isPublic: z.boolean(),
  }),
  demographics: z.object({
    gender: z.string().nullable(),
    age: z.number().nullable(),
    primaryCity: z.string().nullable(),
    closestMajorCity: z.string().nullable(),
    primaryCountry: z.string().nullable(),
    languages: z.string().nullable(),
  }),
  expertise: z.object({
    primary: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
        categoryName: z.string(),
      }),
    ),
    secondary: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
        categoryName: z.string(),
      }),
    ),
  }),
  coaching: z.object({
    formats: z.array(z.string()),
    sessionDeliveryMethods: z.array(z.string()),
    coachingApproaches: z.array(z.string()),
    experienceLevel: z.string().nullable(),
  }),
  faith: z.array(z.string()),
  personality: z.object({
    discType: z.string().nullable(),
    mbtiType: z.string().nullable(),
    enneagramType: z.string().nullable(),
    humanDesignType: z.string().nullable(),
  }),
  status: z.object({
    isFoundingCoach: z.boolean(),
    isFeaturedCoach: z.boolean(),
  }),
  hasEmbedding: z.boolean(),
});

export type CoachMatchingDebugData = z.infer<
  typeof CoachMatchingDebugDataSchema
>;

// Score keys used in matching algorithm
const scoreKeys = [
  "specialty",
  "vectorSimilarity",
  "experienceLevel",
  "location",
  "age",
  "coachingApproach",
  "personality",
  "status",
  "sessionFormat",
  "gender",
  "faith",
] as const;

export type ScoreKey = (typeof scoreKeys)[number];

// Score simulation result
export const SimulatedMatchScoreSchema = z.object({
  scores: z.object({
    specialty: z.number(),
    vectorSimilarity: z.number(),
    experienceLevel: z.number(),
    location: z.number(),
    age: z.number(),
    coachingApproach: z.number(),
    personality: z.number(),
    status: z.number(),
    sessionFormat: z.number(),
    gender: z.number(),
    faith: z.number(),
  }),
  weights: z.object({
    specialty: z.number(),
    vectorSimilarity: z.number(),
    experienceLevel: z.number(),
    location: z.number(),
    age: z.number(),
    coachingApproach: z.number(),
    personality: z.number(),
    status: z.number(),
    sessionFormat: z.number(),
    gender: z.number(),
    faith: z.number(),
  }),
  hardFilters: z.object({
    location: z.boolean(),
    language: z.boolean(),
    sessionDelivery: z.boolean(),
    coachingFormat: z.boolean(),
    faith: z.boolean(),
    gender: z.boolean(),
  }),
  totalWeighted: z.number(),
  normalizedScore: z.number(),
});

export type SimulatedMatchScore = z.infer<typeof SimulatedMatchScoreSchema>;

// Default weights from matching algorithm
export const DEFAULT_MATCHING_WEIGHTS: SimulatedMatchScore["weights"] = {
  specialty: 10.0,
  vectorSimilarity: 3.0,
  experienceLevel: 3.5,
  location: 2.0,
  age: 1.0,
  coachingApproach: 1.0,
  personality: 1.0,
  status: 1.0,
  sessionFormat: 0.1,
  gender: 0.1,
  faith: 0.1,
};

// Weight labels for display
export const WEIGHT_LABELS: Record<ScoreKey, string> = {
  specialty: "Specialty/Expertise",
  vectorSimilarity: "Vector Similarity",
  experienceLevel: "Experience Level",
  location: "Location",
  age: "Age",
  coachingApproach: "Coaching Approach",
  personality: "Personality",
  status: "Status (Founding/Featured)",
  sessionFormat: "Session Format",
  gender: "Gender",
  faith: "Faith Integration",
};

// Order for display (by weight descending)
export const SCORE_DISPLAY_ORDER: ScoreKey[] = [
  "specialty",
  "experienceLevel",
  "vectorSimilarity",
  "location",
  "age",
  "coachingApproach",
  "personality",
  "status",
  "sessionFormat",
  "gender",
  "faith",
];
